import BeachAccessIcon from '@mui/icons-material/BeachAccess'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import DateRangePickerWithPresets from '../../components/DateRangePickerWithPresets/DateRangePickerWithPresets'
import TextInput from '../../components/Form/Inputs/TextInput'
import { useNotification } from '../../store/redux/hooks'
import { useUpdateEmployeeVacationsMutation } from '../../store/redux/reducers/vacationSlice'
import { EmployeeVacation, VacationRequestProps } from '../../store/types/utils/VacationTypes'
import { createVacationErrorMessage } from '../../store/utils/createErrorMessage'
import { formatStringDate } from '../../store/utils/dateUtils'

interface VacationDialogTypes {
  displayEnd: string | null
  displayStart: string | null
  vacationId: string | null
  isOpen: boolean
  onClose: () => void
  selected: EmployeeVacation | null
  descriptionText: string | null
  getEmployeeRequestProps: VacationRequestProps
}

interface FormValues {
  startDate: string
  endDate: string
  description: string
}

const VacationDialog: React.FC<VacationDialogTypes> = ({
  isOpen,
  onClose,
  selected,
  descriptionText,
  vacationId,
  displayStart,
  displayEnd,
  getEmployeeRequestProps,
}) => {
  const { t } = useTranslation()

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<FormValues>({
    defaultValues: {
      startDate: displayStart || '',
      endDate: displayEnd || '',
      description: descriptionText || '',
    },
    mode: 'onChange',
  })
  const { displayNotification } = useNotification()
  const [updateEntry] = useUpdateEmployeeVacationsMutation()

  useEffect(() => {
    setValue('startDate', displayStart || '')
    setValue('endDate', displayEnd || '')
    setValue('description', descriptionText || '')
  }, [displayStart, displayEnd, descriptionText, setValue])

  const watchStartDate = watch('startDate')
  const watchEndDate = watch('endDate')

  const handleClose = () => {
    onClose()
  }

  const updateEmployeeVacation = useCallback(
    async (
      startDate: string,
      endDate: string,
      employeeId: string,
      description: string,
      prevEnd: string,
      prevStart: string,
      vacationId: string
    ) => {
      try {
        await updateEntry({
          dto: {
            startDate,
            endDate,
            employeeId,
            description,
            prevEndDate: prevEnd,
            prevStartDate: prevStart,
            vacationId,
          },
          requestProps: getEmployeeRequestProps,
        }).unwrap()
      } catch (error) {
        displayNotification({
          open: false,
          type: 'error',
          message: createVacationErrorMessage(error),
          timeout: 5000,
        })
      }
    },
    [updateEntry, displayNotification, getEmployeeRequestProps]
  )

  const onSubmit = (data: FormValues) => {
    if (
      data.startDate &&
      data.endDate &&
      selected &&
      vacationId &&
      displayEnd &&
      displayStart &&
      dayjs(data.startDate).isValid() &&
      dayjs(data.endDate).isValid()
    ) {
      const employeeId = selected.id.toString()
      updateEmployeeVacation(
        data.startDate,
        data.endDate,
        employeeId,
        data.description,
        displayEnd,
        displayStart,
        vacationId
      )
      handleClose()
    }
  }

  const vacationRangeDate = useMemo(() => {
    if (!watchStartDate) {
      return ''
    }

    const formattedStartDate = formatStringDate(watchStartDate)

    if (watchStartDate === watchEndDate) {
      return formattedStartDate
    }

    const formattedEndDate = formatStringDate(watchEndDate)

    return `${formattedStartDate} - ${formattedEndDate}`
  }, [watchStartDate, watchEndDate])

  const isFormValid = dayjs(watchStartDate).isValid() && dayjs(watchEndDate).isValid() && displayStart

  return (
    <Dialog className={isOpen ? 'vacation-dialog-open' : ''} open={isOpen} onClose={handleClose}>
      <DialogTitle>
        <Stack direction="row" spacing={10}>
          <Stack direction="column" spacing={2}>
            {selected && (
              <Typography
                component="h3"
                variant="h6"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  wrap: 'break-space',
                }}
              >
                {selected.name}
              </Typography>
            )}
            <Stack direction="row" spacing={1}>
              <BeachAccessIcon />
              <Typography variant="body1">{vacationRangeDate}</Typography>
            </Stack>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={2} sx={{ marginTop: 2 }}>
          <Controller
            name={'startDate'}
            control={control}
            rules={{
              required: t('page.vacationView.form.startDateRequired'),
              validate: {
                valid: date => {
                  if (!date) return true
                  return dayjs(date.toString()).isValid() || t('page.usersProfile.form.errors.birthDateInvalid')
                },
              },
            }}
            render={({ field: { onChange: onStartChange, value: startValue }, fieldState: { error: startError } }) => (
              <Controller
                name={'endDate'}
                control={control}
                rules={{
                  required: t('page.vacationView.form.endDateRequired'),
                  validate: {
                    valid: date => {
                      if (date === null || date === undefined) return true
                      return dayjs(date.toString()).isValid() || t('page.usersProfile.form.errors.birthDateInvalid')
                    },
                    endDateAfterStartDate: value => {
                      const dayjsValue = dayjs(value)
                      if (dayjs.isDayjs(dayjsValue)) {
                        const startDate = watch('startDate')
                        if (dayjsValue.isBefore(startDate)) {
                          return t('page.vacationView.form.endDateBefore')
                        }
                      }
                      return true
                    },
                  },
                }}
                render={({ field: { onChange: onEndChange, value: endValue }, fieldState: { error: endError } }) => (
                  <Box>
                    <DateRangePickerWithPresets
                      startDate={startValue ? dayjs(startValue) : null}
                      endDate={endValue ? dayjs(endValue) : null}
                      setStartDate={newDate => onStartChange(newDate ? newDate.format('YYYY-MM-DD') : '')}
                      setEndDate={newDate => onEndChange(newDate ? newDate.format('YYYY-MM-DD') : '')}
                      startDateLabel={`${t('page.vacationView.messages.start')} *`}
                      endDateLabel={`${t('page.vacationView.messages.end')} *`}
                      presetsType={'future'}
                      selectedDate={displayStart}
                    />
                    {startError && (
                      <Typography variant="caption" color="error">
                        {startError.message}
                      </Typography>
                    )}
                    {endError && (
                      <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                        {endError.message}
                      </Typography>
                    )}
                  </Box>
                )}
              />
            )}
          />
          <TextInput
            name="description"
            control={control}
            label={t('page.vacationView.messages.desc')}
            placeholder={t('page.vacationView.enterDescription')}
            maxLength={500}
            rules={{
              maxLength: {
                value: 500,
                message: t('page.worklog.form.descriptionLengthError'),
              },
            }}
            multiline
            rows={4}
            showCharCounter
          />
          <Stack direction="row" spacing={2} justifyContent="right">
            <Button variant="text" color="primary" onClick={handleClose}>
              <CloseIcon sx={{ fontSize: '15px', right: '1px', position: 'relative' }} />
              {t('page.vacationView.cancel')}
            </Button>
            <Button
              disabled={!isDirty || !isFormValid}
              onClick={handleSubmit(onSubmit)}
              variant="contained"
              color="primary"
            >
              <CheckIcon sx={{ fontSize: '15px', right: '1px', position: 'relative' }} />
              {t('page.vacationView.save')}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

export default VacationDialog
